import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { propTypes } from './props';
import { Container, EditorBox, InnerContainer, InnerContainerWrapper, Title } from './styled';
import { TopToolBar } from '../TopToolBar';
import { BottomToolBar } from '../BottomToolBar';
import { elementTypes, sideBarpillsList } from '@/constants';
import {
    Stage,
    Layer,
    Rect,
    Circle,
    Transformer,
    Arrow,
    RegularPolygon,
    Path,
    Star,
    Line,
    Group,
} from 'react-konva';
import { CanvasImage } from '../CanvasImage';
import theme from '@/theme';
import { QrElement } from '../QrElement';
import { EditableText } from '../EditableText';
import { debounce } from '@/utils';
import { useKonvaSnapping } from './util';
import { TEXT_DICTIONARY } from '@/constants/textConstants';

export const Editor = ({
    setLoading,
    selectedTab,
    languageLocale,
    title = TEXT_DICTIONARY?.[languageLocale]?.TITLE,
    zoomPercentage,
    onChangeZoomPercentage,
    editorHeight,
    editorWidth,
    onSetBackgroundColor,
    elements,
    history,
    redoStack,
    onSetBackgroundImage,
    onUndo,
    onRedo,
    shapeStrokeColor,
    shapeStrokeWidth,
    shapeFillColor,
    onChangeShapeStrokeColor,
    onChangeShapeStrokeWidth,
    onChangeShapeFill,
    shapeOpacity,
    onChangeShapeOpacity,
    selectedElement,
    onSelectElement,
    stageRef,
    saveHistory,
    onChangeBackgroundImageOpacity,
    backgroundImageOpacity,
    onDeleteSelectedElement,
    onCopySelectedElement,
    onToggleLockElement,
    bringSelectedElementToFront,
    sendSelectedElementToBack,
    imageStrokeWidth,
    imageStrokeColor,
    imageOpacity,
    onChangeImageStrokeWidth,
    onChangeImageStrokeColor,
    onChangeImageOpacity,
    imageTintColor,
    imageTintStrength,
    onChangeImageTintColor,
    onChangeImageTintStrength,
    onClearImageTint,
    qrStrokeWidth,
    qrStrokeColor,
    qrOpacity,
    onChangeQrStrokeWidth,
    onChangeQrStrokeColor,
    onChangeQrOpacity,
    defaultTextProps,
    onChangeTextProperty,
    onSetPageSize,
    selectedPageSize,
    selectedPageSizeLabel,
    loadingImages,
    loadingFonts,
    uploadImageCallBack,
    setLoadingUploadImage,
    cuttingGuideStroke,
    cuttingGuideStrokeColor,
    onChangeCuttingGuideProp,
    onSave,
    showSaveButton,
    onSetSelectedTab,
    saveButtonText,
    onRemoveBackgroundImage,
    showBackgroundImagePicker,
    showOpacityPicker,
    changeSelectedElementProperty,
    showOutsideBoundaryPreview = false,
    outsideBoundaryPreviewPadding = 80,
}) => {
    const transformerRef = useRef(null);
    const previewPadding = useMemo(
        () =>
            showOutsideBoundaryPreview
                ? Math.max(0, Number(outsideBoundaryPreviewPadding) || 0)
                : 0,
        [showOutsideBoundaryPreview, outsideBoundaryPreviewPadding],
    );
    const stageWidth = useMemo(
        () => editorWidth + previewPadding * 2,
        [editorWidth, previewPadding],
    );
    const stageHeight = useMemo(
        () => editorHeight + previewPadding * 2,
        [editorHeight, previewPadding],
    );
    const pageOffsetX = previewPadding;
    const pageOffsetY = previewPadding;
    useEffect(() => {
        if (transformerRef?.current) {
            const selectedNode = stageRef?.current?.findOne(`#${selectedElement?.id}`);
            if (selectedNode) {
                transformerRef?.current.nodes([selectedNode]);
                transformerRef?.current.getLayer().batchDraw();
            } else {
                transformerRef?.current.nodes([]);
            }
        }
    }, [selectedElement]);
    const { handleDragging, handleDragEnd, handleResizing, handleResizeEnd } = useKonvaSnapping({
        guidelineColor: 'blue',
        guidelineDash: true,
        snapToStageCenter: true,
        snapRange: 5,
        guidelineThickness: 1,
        showGuidelines: true,
        snapToShapes: true,
        snapToStageBorders: true,
    });
    // console.error({ selectedElement });
    let canvasBackgroundColor = useMemo(() => {
        let backgroundColor;
        let backgroundIndex = elements?.findIndex(e => e?.type === elementTypes?.backgroundColor);

        if (backgroundIndex > -1) {
            backgroundColor = elements?.[backgroundIndex]?.color;
        }
        return backgroundColor;
    }, [elements]);

    let canvasBackgroundImage = useMemo(() => {
        let backgroundImage;

        let backgroundImageIndex = elements?.findIndex(
            e => e?.type === elementTypes?.backgroundImage,
        );
        if (backgroundImageIndex > -1) {
            backgroundImage = elements?.[backgroundImageIndex];
        }
        return backgroundImage;
    }, [elements]);

    const handleTransform = (id, newAttrs) => {
        let newAttributes = { ...newAttrs };
        delete newAttributes['ref'];
        const updatedElements = elements?.map(el =>
            el.id === id ? { ...el, ...newAttributes } : el,
        );
        saveHistory(updatedElements);
        onSelectElement({ ...newAttributes });
    };
    const handleSelect = elem => {
        onSelectElement(elem);
    };

    const debouncedTransform = debounce((id, attrs) => {
        const updatedElements = elements?.map(el =>
            el.id === id
                ? {
                      ...el,
                      ...attrs,
                  }
                : el,
        );
        saveHistory(updatedElements);
        onSelectElement({
            ...attrs,
        });
    }, 100);

    const debouncedChangeText = debounce(e => {
        onChangeTextProperty('text', e);
    }, 300);

    const handleTextTransform = (id, attrs) => {
        debouncedTransform(id, attrs);
    };

    const onChangeTextContent = e => {
        debouncedChangeText(e);
    };
    const renderElements = useCallback(
        el => {
            switch (el.type) {
                case elementTypes.circle:
                    return (
                        <Circle
                            key={el.id}
                            {...el}
                            id={el.id}
                            onClick={() => handleSelect(el)}
                            onDragEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                                handleDragEnd(e);
                            }}
                            onDragMove={handleDragging}
                            onTransformEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                            }}
                            strokeScaleEnabled={false}
                        />
                    );
                case elementTypes.square:
                    return (
                        <Rect
                            key={el.id}
                            {...el}
                            id={el.id}
                            cornerRadius={el.cornerRadius || 0}
                            onClick={() => handleSelect(el)}
                            onDragEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                                handleDragEnd(e);
                            }}
                            onDragMove={handleDragging}
                            onTransformEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                            }}
                            strokeScaleEnabled={false}
                        />
                    );
                case elementTypes.arrowRight:
                    return (
                        <Arrow
                            key={el.id}
                            {...el}
                            id={el.id}
                            onClick={() => handleSelect(el)}
                            onDragEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                                handleDragEnd(e);
                            }}
                            onDragMove={handleDragging}
                            onTransformEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                            }}
                            strokeScaleEnabled={false}
                        />
                    );
                case elementTypes.triangle:
                    return (
                        <RegularPolygon
                            key={el.id}
                            {...el}
                            id={el.id}
                            onClick={() => handleSelect(el)}
                            onDragEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                                handleDragEnd(e);
                            }}
                            onDragMove={handleDragging}
                            onTransformEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                            }}
                            strokeScaleEnabled={false}
                        />
                    );
                case elementTypes.heart:
                    return (
                        <Path
                            key={el.id}
                            {...el}
                            id={el.id}
                            onClick={() => handleSelect(el)}
                            onDragEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                                handleDragEnd(e);
                            }}
                            onDragMove={handleDragging}
                            onTransformEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                            }}
                            strokeScaleEnabled={false}
                        />
                    );
                case elementTypes.star:
                    return (
                        <Star
                            key={el.id}
                            {...el}
                            id={el.id}
                            onClick={() => handleSelect(el)}
                            onDragEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                                handleDragEnd(e);
                            }}
                            onDragMove={handleDragging}
                            onTransformEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                            }}
                            strokeScaleEnabled={false}
                        />
                    );
                case elementTypes.horizontalLine:
                    return (
                        <Line
                            key={el.id}
                            {...el}
                            id={el.id}
                            onClick={() => handleSelect(el)}
                            onDragEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                                handleDragEnd(e);
                            }}
                            onDragMove={handleDragging}
                            onTransformEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                            }}
                            strokeScaleEnabled={false}
                        />
                    );
                case elementTypes.verticalLine:
                    return (
                        <Line
                            key={el.id}
                            {...el}
                            id={el.id}
                            onClick={() => handleSelect(el)}
                            onDragEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                                handleDragEnd(e);
                            }}
                            onDragMove={handleDragging}
                            onTransformEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                            }}
                            strokeScaleEnabled={false}
                        />
                    );
                case elementTypes.image:
                    return (
                        <CanvasImage
                            key={el.id}
                            id={el.id}
                            element={{
                                ...el,
                            }}
                            onClick={() => handleSelect(el)}
                            onDragEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                                handleDragEnd(e);
                            }}
                            onDragMove={handleDragging}
                            onTransformEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                            }}
                            setLoading={setLoading}
                        />
                    );
                case elementTypes.qr:
                    return (
                        <QrElement
                            key={el.id}
                            element={el}
                            onClick={() => handleSelect(el)}
                            onDragEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                                handleDragEnd(e);
                            }}
                            onTransformEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                            }}
                            onDragMove={handleDragging}
                            languageLocale={languageLocale}
                            LogoTitle={TEXT_DICTIONARY?.[languageLocale]?.LOGO}
                        />
                    );
                case elementTypes.text:
                    return (
                        <EditableText
                            trRef={transformerRef}
                            key={el.id}
                            id={el.id}
                            element={el}
                            onClick={() => handleSelect(el)}
                            onTransform={e => handleTextTransform(el.id, e)}
                            onDragEnd={e => {
                                handleTransform(el.id, e.target.attrs);
                                handleDragEnd(e);
                            }}
                            onDragMove={handleDragging}
                            onChangeTextContent={onChangeTextContent}
                        />
                    );
                default:
                    return null;
            }
        },
        [elements, selectedElement, languageLocale],
    );

    return (
        <Container selectedTab={selectedTab}>
            {selectedTab && selectedTab !== sideBarpillsList?.whiteLabel && (
                <TopToolBar
                    selectedTab={selectedTab}
                    onSetBackgroundColor={onSetBackgroundColor}
                    elements={elements}
                    canvasBackgroundColor={canvasBackgroundColor}
                    onSetBackgroundImage={onSetBackgroundImage}
                    canvasBackgroundImage={canvasBackgroundImage}
                    shapeStrokeColor={shapeStrokeColor}
                    shapeStrokeWidth={shapeStrokeWidth}
                    shapeFillColor={shapeFillColor}
                    onChangeShapeStrokeColor={onChangeShapeStrokeColor}
                    onChangeShapeStrokeWidth={onChangeShapeStrokeWidth}
                    onChangeShapeFill={onChangeShapeFill}
                    shapeOpacity={shapeOpacity}
                    onChangeShapeOpacity={onChangeShapeOpacity}
                    selectedElement={selectedElement}
                    onChangeBackgroundImageOpacity={onChangeBackgroundImageOpacity}
                    backgroundImageOpacity={backgroundImageOpacity}
                    imageStrokeWidth={imageStrokeWidth}
                    imageStrokeColor={imageStrokeColor}
                    imageOpacity={imageOpacity}
                    onChangeImageStrokeWidth={onChangeImageStrokeWidth}
                    onChangeImageStrokeColor={onChangeImageStrokeColor}
                    onChangeImageOpacity={onChangeImageOpacity}
                    imageTintColor={imageTintColor}
                    imageTintStrength={imageTintStrength}
                    onChangeImageTintColor={onChangeImageTintColor}
                    onChangeImageTintStrength={onChangeImageTintStrength}
                    onClearImageTint={onClearImageTint}
                    qrStrokeWidth={qrStrokeWidth}
                    qrStrokeColor={qrStrokeColor}
                    qrOpacity={qrOpacity}
                    onChangeQrStrokeWidth={onChangeQrStrokeWidth}
                    onChangeQrStrokeColor={onChangeQrStrokeColor}
                    onChangeQrOpacity={onChangeQrOpacity}
                    defaultTextProps={defaultTextProps}
                    onChangeTextProperty={onChangeTextProperty}
                    onSetPageSize={onSetPageSize}
                    selectedPageSize={selectedPageSize}
                    selectedPageSizeLabel={selectedPageSizeLabel}
                    languageLocale={languageLocale}
                    uploadImageCallBack={uploadImageCallBack}
                    setLoadingUploadImage={setLoadingUploadImage}
                    cuttingGuideStroke={cuttingGuideStroke}
                    cuttingGuideStrokeColor={cuttingGuideStrokeColor}
                    onChangeCuttingGuideProp={onChangeCuttingGuideProp}
                    onRemoveBackgroundImage={onRemoveBackgroundImage}
                    showBackgroundImagePicker={showBackgroundImagePicker}
                    showOpacityPicker={showOpacityPicker}
                    changeSelectedElementProperty={changeSelectedElementProperty}
                />
            )}
            <InnerContainer>
                <InnerContainerWrapper editorHeight={stageHeight + 50} editorWidth={stageWidth}>
                    {title && <Title>{title}</Title>}
                    <EditorBox editorHeight={stageHeight} editorWidth={stageWidth}>
                        {!loadingImages && !loadingFonts && (
                            <Stage
                                width={stageWidth}
                                height={stageHeight}
                                ref={stageRef}
                                onMouseDown={e => {
                                    if (e.target === e.target.getStage()) {
                                        onSelectElement(null);
                                        onSetSelectedTab?.(sideBarpillsList.template);
                                    }
                                }}
                            >
                                <Layer
                                    onClick={() => {
                                        onSelectElement(null);
                                        onSetSelectedTab?.(sideBarpillsList.template);
                                    }}
                                    listening={false}
                                >
                                    <Rect
                                        x={pageOffsetX}
                                        y={pageOffsetY}
                                        width={editorWidth}
                                        height={editorHeight}
                                        fill={canvasBackgroundColor || theme.color.white}
                                    />
                                    {canvasBackgroundImage && (
                                        <CanvasImage
                                            element={{
                                                ...canvasBackgroundImage,
                                                x: pageOffsetX,
                                                y: pageOffsetY,
                                                width: editorWidth,
                                                height: editorHeight,
                                            }}
                                        />
                                    )}
                                </Layer>
                                <Layer>
                                    <Group
                                        x={pageOffsetX}
                                        y={pageOffsetY}
                                        scaleX={zoomPercentage / 100}
                                        scaleY={zoomPercentage / 100}
                                    >
                                        {elements?.map(el => renderElements(el))}
                                        {selectedElement && (
                                            <Transformer
                                                ref={transformerRef}
                                                onTransform={handleResizing}
                                                onTransformEnd={handleResizeEnd}
                                                enabledAnchors={
                                                    selectedElement?.draggable
                                                        ? selectedElement?.type ===
                                                          elementTypes?.text
                                                            ? [
                                                                  'top-left',
                                                                  'top-right',
                                                                  'bottom-left',
                                                                  'bottom-right',
                                                                  'middle-left',
                                                                  'middle-right',
                                                              ]
                                                            : [
                                                                  'top-left',
                                                                  'top-right',
                                                                  'bottom-left',
                                                                  'bottom-right',
                                                                  'middle-left',
                                                                  'middle-right',
                                                                  'top-center',
                                                                  'bottom-center',
                                                              ]
                                                        : []
                                                }
                                                borderStroke={theme.color.primary}
                                                borderStrokeWidth={2}
                                                // add anchors
                                                anchorFill={theme.color.white}
                                                anchorStroke={theme.color.primary}
                                                anchorStrokeWidth={2}
                                                anchorSize={8}
                                                rotateEnabled={
                                                    selectedElement?.draggable ? true : false
                                                }
                                                // make all anchors look like circles
                                                // anchorCornerRadius={50}
                                                boundBoxFunc={(oldBox, newBox) => {
                                                    // limit resize
                                                    if (
                                                        !selectedElement?.draggable ||
                                                        // Handle element flliping
                                                        newBox.width < 1 ||
                                                        newBox.height < 1
                                                    ) {
                                                        return oldBox;
                                                    }
                                                    return newBox;
                                                }}
                                            />
                                        )}
                                    </Group>
                                </Layer>
                                {showOutsideBoundaryPreview && previewPadding > 0 ? (
                                    <Layer listening={false}>
                                        <Rect
                                            listening={false}
                                            x={0}
                                            y={0}
                                            width={stageWidth}
                                            height={pageOffsetY}
                                            fill="rgba(0, 0, 0, 0.14)"
                                        />
                                        <Rect
                                            listening={false}
                                            x={0}
                                            y={pageOffsetY}
                                            width={pageOffsetX}
                                            height={editorHeight}
                                            fill="rgba(0, 0, 0, 0.14)"
                                        />
                                        <Rect
                                            listening={false}
                                            x={pageOffsetX + editorWidth}
                                            y={pageOffsetY}
                                            width={stageWidth - (pageOffsetX + editorWidth)}
                                            height={editorHeight}
                                            fill="rgba(0, 0, 0, 0.14)"
                                        />
                                        <Rect
                                            listening={false}
                                            x={0}
                                            y={pageOffsetY + editorHeight}
                                            width={stageWidth}
                                            height={stageHeight - (pageOffsetY + editorHeight)}
                                            fill="rgba(0, 0, 0, 0.14)"
                                        />
                                    </Layer>
                                ) : null}
                                <Layer listening={false}>
                                    <Rect
                                        listening={false}
                                        x={pageOffsetX}
                                        y={pageOffsetY}
                                        width={editorWidth}
                                        height={editorHeight}
                                        stroke={cuttingGuideStrokeColor}
                                        strokeWidth={cuttingGuideStroke}
                                        strokeScaleEnabled={false}
                                        fill={'transparent'}
                                        dash={[10, 5]} // [dash length, gap length]
                                    />
                                </Layer>
                            </Stage>
                        )}
                    </EditorBox>
                </InnerContainerWrapper>
            </InnerContainer>
            <BottomToolBar
                disableUndo={history.length === 0}
                disableRedo={redoStack.length === 0}
                selectedTab={selectedTab}
                zoomPercentage={zoomPercentage}
                onChangeZoomPercentage={onChangeZoomPercentage}
                onUndo={onUndo}
                onRedo={onRedo}
                selectedElement={selectedElement}
                onDeleteSelectedElement={onDeleteSelectedElement}
                onCopySelectedElement={onCopySelectedElement}
                onToggleLockElement={onToggleLockElement}
                bringSelectedElementToFront={bringSelectedElementToFront}
                sendSelectedElementToBack={sendSelectedElementToBack}
                languageLocale={languageLocale}
                onSave={onSave}
                showSaveButton={showSaveButton}
                saveButtonText={saveButtonText}
            />
        </Container>
    );
};

Editor.propTypes = propTypes;
